import { useState, useEffect, useCallback, useMemo } from 'react';
import { SelectChangeEvent } from '@mui/material';
import { InventoryTransfersService, LocationsService } from '../../../api';
import { getVisiblePaginationItems } from '../../common/Pagination';
import { groupBy } from '../../../utils/helpers/array';
import { formatTransferEventDateEs } from '../../../utils/inventory-transfers/transferDisplayDate';
import {
  INV_STATUS_BRANCH_LABEL,
  INV_STATUS_WAREHOUSE_LABEL,
} from '../../../constants/inventory-status-labels';
import { TRANSFER_MESSAGES } from '../../../utils/constants';
import type { NotificationSeverity } from '../../../utils/constants/notificationMessages';
import {
  downloadCsvExport,
  csvExportDateSuffix,
  CsvExportError,
} from '../../../utils/csvExport/downloadCsvExport';
import { normalizeScannedProductCode } from '../../../utils/normalizeScannedProductCode';
import { printInventoryTransfer } from '../../../utils/printInventoryTransfer';
import { openBlankPrintWindow, PRINT_POPUP_BLOCKED_MESSAGE } from '../../../utils/printWindow';

// Interfaces
export interface TransferOrder {
  id: string;
  transferCode: string;
  destination: string;
  /** Destination location id — used to gate accept to destination staff. */
  destinationLocationId: number | null;
  /** Ubicación de origen (almacén o sucursal); "—" si no aplica o no está definida. */
  source: string;
  productCount: number;
  date: string;
  status: string;
  createdBy?: string;
  createdAt: string;
}

export interface FilterState {
  transferCode: string;
  location: string;
  sourceLocation: string;
  dateFrom: string;
  dateTo: string;
  status: string;
}

export interface PaginationState {
  total: number;
  currentPage: number;
  perPage: number;
  totalPages: number;
  itemsPerPage: number;
}

export interface TransferDetail {
  id: string;
  transferCode: string;
  destination: string;
  source: string;
  createdBy: string;
  /** Present when the transfer was accepted at destination. */
  completedBy?: string | null;
  /** Present when the transfer was cancelled/rejected. */
  cancelledBy?: string | null;
  date: string;
  status: string;
  items: TransferItem[];
  notes?: string;
  requireCounting?: boolean;
  inventoryRequestId?: number | null;
  inventoryRequestCode?: string | null;
}

export interface TransferItem {
  id: string;
  /** warehouse_inventory_products.id — for detail view in transfer context */
  warehouseInventoryProductId?: number;
  productCode: string;
  productName: string;
  productType: string;
  recordCode: string;
  status: string;
  location: string;
  quantity: number;
  costPrice?: number;
  salePrice?: number;
}

export function sumTransferItemsCost(items: TransferItem[]): number {
  return items.reduce((total, item) => total + (item.costPrice ?? 0), 0);
}

export function sumTransferItemsSale(items: TransferItem[]): number {
  return items.reduce((total, item) => total + (item.salePrice ?? 0), 0);
}

function mapApiTransferItemToTransferItem(item: any): TransferItem {
  const wip = item.warehouseInventoryProduct;
  const product = wip?.product;

  return {
    id: item.id?.toString() || Math.random().toString(),
    warehouseInventoryProductId: wip?.id != null ? Number(wip.id) : undefined,
    productCode: wip?.productCodeUnique || product?.code || item.productCode || 'N/A',
    productName: product?.name || item.productName || 'N/A',
    productType: product?.category?.name || item.productType || 'N/A',
    recordCode: wip?.inventoryRecordItem?.record?.recordCode || item.recordCode || 'N/A',
    status:
      wip?.status === 'ASSIGNED'
        ? INV_STATUS_BRANCH_LABEL
        : wip?.status === 'IN_TRANSIT'
          ? 'En Tránsito'
          : item.status === 'ASSIGNED'
            ? INV_STATUS_BRANCH_LABEL
            : item.status === 'IN_TRANSIT'
              ? 'En Tránsito'
              : INV_STATUS_WAREHOUSE_LABEL,
    location: wip?.location?.name || item.location || 'N/A',
    quantity: 1,
    costPrice: Number(wip?.costPrice) || Number(product?.costPrice) || 0,
    salePrice: Number(wip?.salePrice) || Number(product?.salePrice) || 0,
  };
}

export interface UseInventoryTransferProps {
  onTransferUpdate?: () => void;
}

interface LocationOption {
  id: number;
  name: string;
}

const mapUiStatusToApiStatus = (
  status: string,
): 'PENDING' | 'COMPLETED' | 'CANCELLED' | undefined => {
  if (status === 'Pendiente') return 'PENDING';
  if (status === 'Completado') return 'COMPLETED';
  if (status === 'Cancelado') return 'CANCELLED';
  return undefined;
};

/** Origen en cabecera de transferencia; puede faltar si hay varias ubicaciones de origen. */
const getTransferSourceDisplayName = (transfer: {
  sourceLocation?: { name?: string } | null;
}): string => {
  const name = transfer?.sourceLocation?.name?.trim();
  return name || '—';
};

function formatTransferUserName(
  user?: { firstName?: string | null; lastName?: string | null } | null,
): string | null {
  if (user == null) return null;
  const name = `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim();
  return name !== '' ? name : null;
}

function mapApiStatusToTransferLabel(status?: string): string {
  if (status === 'PENDING') return 'Pendiente';
  if (status === 'COMPLETED') return 'Completado';
  if (status === 'CANCELLED') return 'Cancelado';
  return 'Pendiente';
}

/** Minimal shape used when mapping transfer API payloads into TransferDetail. */
type ApiTransferUser = {
  firstName?: string | null;
  lastName?: string | null;
};

type ApiTransferPayload = {
  id?: number | string;
  transferCode?: string;
  destinationLocation?: { name?: string };
  sourceLocation?: { name?: string } | null;
  createdBy?: ApiTransferUser | null;
  completedBy?: ApiTransferUser | null;
  cancelledBy?: ApiTransferUser | null;
  createdAt?: string;
  updatedAt?: string;
  completedAt?: string;
  cancelledAt?: string;
  status?: string;
  notes?: string;
  requireCounting?: boolean;
  inventoryRequestId?: number | null;
  inventoryRequest?: { id?: number; requestCode?: string | null } | null;
};

function mapApiTransferToDetail(
  transfer: ApiTransferPayload | null | undefined,
  orderId: string,
  items: TransferItem[],
  extras: Partial<TransferDetail> = {},
): TransferDetail {
  return {
    id: transfer?.id?.toString() || orderId,
    transferCode: transfer?.transferCode || `TR-${orderId}`,
    destination: transfer?.destinationLocation?.name || 'No especificado',
    source: getTransferSourceDisplayName(transfer || {}),
    createdBy: formatTransferUserName(transfer?.createdBy) ?? 'N/A',
    completedBy: formatTransferUserName(transfer?.completedBy),
    cancelledBy: formatTransferUserName(transfer?.cancelledBy),
    date: formatTransferEventDateEs(transfer),
    status: mapApiStatusToTransferLabel(transfer?.status),
    notes: transfer?.notes || undefined,
    inventoryRequestId:
      transfer?.inventoryRequestId != null
        ? Number(transfer.inventoryRequestId)
        : transfer?.inventoryRequest?.id != null
          ? Number(transfer.inventoryRequest.id)
          : null,
    inventoryRequestCode: transfer?.inventoryRequest?.requestCode ?? null,
    items,
    ...extras,
  };
}

const EMPTY_FILTER_STATE: FilterState = {
  transferCode: '',
  location: '',
  sourceLocation: '',
  dateFrom: '',
  dateTo: '',
  status: '',
};

export const useInventoryTransfer = ({ onTransferUpdate }: UseInventoryTransferProps = {}) => {
  // State
  const [transferOrders, setTransferOrders] = useState<TransferOrder[]>([]);
  const [locations, setLocations] = useState<LocationOption[]>([]);
  // `filters` (draft) is bound to the filter inputs and updates on every
  // keystroke. `appliedFilters` only changes on Search/Reset and is what
  // actually drives `fetchTransfers` and the CSV export — this is what
  // stops the list from refetching on every keystroke while typing a
  // transfer code or picking a date.
  const [filters, setFilters] = useState<FilterState>(EMPTY_FILTER_STATE);
  const [appliedFilters, setAppliedFilters] = useState<FilterState>(EMPTY_FILTER_STATE);
  const [isLoading, setIsLoading] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [pagination, setPagination] = useState<PaginationState>({
    total: 0,
    currentPage: 1,
    perPage: 10,
    totalPages: 0,
    itemsPerPage: 10,
  });

  // Estado para controlar la expansión de filtros
  const [showMoreFilters, setShowMoreFilters] = useState(false);

  // Modal state
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedTransferDetail, setSelectedTransferDetail] = useState<TransferDetail | null>(null);
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);

  // Estado para el modal de confirmación
  const [showConfirmationDialog, setShowConfirmationDialog] = useState(false);
  const [confirmationType, setConfirmationType] = useState<'accept' | 'reject'>('accept');
  const [notification, setNotification] = useState<{
    open: boolean;
    message: string;
    severity: NotificationSeverity;
  }>({
    open: false,
    message: '',
    severity: 'success',
  });
  const [transferIdToConfirm, setTransferIdToConfirm] = useState<string | null>(null);
  const [showFinalConfirmModal, setShowFinalConfirmModal] = useState(false);
  const [isConfirming, setIsConfirming] = useState(false);

  // Estado para el modal de impresión

  // Estado para productos seleccionados (conteo)
  const [selectedProductIds, setSelectedProductIds] = useState<Set<string>>(new Set());
  const [barcodeInput, setBarcodeInput] = useState('');
  const [draftSaved, setDraftSaved] = useState(false);
  const [draftLastSaved, setDraftLastSaved] = useState<Date | null>(null);

  // Estado para actualizar el flag de conteo
  const [isUpdatingCountingFlag, setIsUpdatingCountingFlag] = useState(false);
  const [searchTrigger, setSearchTrigger] = useState(0);

  // Estado para grupos de productos expandidos (por tipo)
  const [expandedProductTypes, setExpandedProductTypes] = useState<Set<string>>(new Set());

  // Función para calcular páginas totales
  const calculateTotalPages = useCallback((total: number, itemsPerPage: number) => {
    return Math.ceil(total / itemsPerPage);
  }, []);

  // Función para cambiar de página
  const handlePageChange = useCallback((page: number) => {
    setPagination((prev) => ({
      ...prev,
      currentPage: page,
    }));
  }, []);

  // Función para cambiar elementos por página
  const handleItemsPerPageChange = useCallback(
    (event: SelectChangeEvent<unknown>) => {
      const newItemsPerPage = Number(event.target.value);
      setPagination((prev) => ({
        ...prev,
        itemsPerPage: newItemsPerPage,
        currentPage: 1,
        totalPages: calculateTotalPages(prev.total, newItemsPerPage),
      }));
      setSearchTrigger((prev) => prev + 1);
    },
    [calculateTotalPages],
  );

  // Cargar ubicaciones/sucursales al inicio
  useEffect(() => {
    const fetchLocations = async () => {
      try {
        const response = await LocationsService.locationsControllerFindAll();
        if (response && response.data) {
          setLocations(
            response.data.map((location: any) => ({
              id: Number(location.id),
              name: location.name,
            })),
          );
        }
      } catch (error) {
        console.error('Error al cargar ubicaciones:', error);
      }
    };

    fetchLocations();
  }, []);

  // Función para actualizar el flag de conteo de una transferencia específica
  const updateTransferRequireCounting = useCallback(
    async (transferId: string, requireCounting: boolean) => {
      try {
        setIsUpdatingCountingFlag(true);

        await InventoryTransfersService.inventoryTransfersControllerUpdateRequireCounting(
          Number(transferId),
          {
            requireCounting,
          },
        );

        // Recargar la transferencia completa para asegurar que tenemos todos los datos actualizados
        const transferResponse =
          await InventoryTransfersService.inventoryTransfersControllerFindOne(transferId);
        const freshTransfer = transferResponse?.data || transferResponse;

        // Actualizar el estado local de la transferencia
        if (selectedTransferDetail && selectedTransferDetail.id === transferId) {
          setSelectedTransferDetail({
            ...selectedTransferDetail,
            requireCounting:
              freshTransfer?.requireCounting !== undefined
                ? freshTransfer.requireCounting
                : requireCounting,
          });
        }
      } catch (error) {
        console.error('Error al actualizar flag de conteo:', error);
        throw error;
      } finally {
        setIsUpdatingCountingFlag(false);
      }
    },
    [selectedTransferDetail],
  );

  // Interfaz para el borrador
  interface TransferDraft {
    transferId: string;
    selectedProductIds: string[];
    savedAt: string;
    transferCode: string;
  }

  // Clave para localStorage basada en transferId
  const getDraftKey = useCallback((transferId: string) => {
    return `transfer_draft_${transferId}`;
  }, []);

  // Función para guardar borrador (solo manual)
  const saveDraft = useCallback(
    (transferId: string, transferCode: string) => {
      if (!transferId || selectedProductIds.size === 0) return;

      const draft: TransferDraft = {
        transferId,
        selectedProductIds: Array.from(selectedProductIds),
        savedAt: new Date().toISOString(),
        transferCode,
      };

      try {
        localStorage.setItem(getDraftKey(transferId), JSON.stringify(draft));
        setDraftLastSaved(new Date());
        setDraftSaved(true);

        // Ocultar notificación después de 3 segundos
        setTimeout(() => setDraftSaved(false), 3000);
      } catch (error) {
        console.error('Error al guardar borrador:', error);
      }
    },
    [selectedProductIds, getDraftKey],
  );

  // Función para cargar borrador
  const loadDraft = useCallback(
    (transferId: string): TransferDraft | null => {
      try {
        const draftData = localStorage.getItem(getDraftKey(transferId));
        if (draftData) {
          return JSON.parse(draftData) as TransferDraft;
        }
      } catch (error) {
        console.error('Error al cargar borrador:', error);
      }
      return null;
    },
    [getDraftKey],
  );

  // Función para eliminar borrador
  const clearDraft = useCallback(
    (transferId: string) => {
      try {
        localStorage.removeItem(getDraftKey(transferId));
        setSelectedProductIds(new Set());
        setDraftSaved(false);
        setDraftLastSaved(null);
      } catch (error) {
        console.error('Error al eliminar borrador:', error);
      }
    },
    [getDraftKey],
  );

  // Función para resetear conteo (usado cuando se quiere empezar nuevo conteo)
  const resetCounting = useCallback(
    (transferId: string) => {
      clearDraft(transferId);
      setSelectedProductIds(new Set());
      setDraftLastSaved(null);
    },
    [clearDraft],
  );

  // Función para toggle de selección
  const toggleProductSelection = useCallback((productId: string) => {
    setSelectedProductIds((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(productId)) {
        newSet.delete(productId);
      } else {
        newSet.add(productId);
      }
      return newSet;
    });
  }, []);

  const selectProductIds = useCallback((ids: string[]) => {
    if (ids.length === 0) return;
    setSelectedProductIds((prev) => {
      const newSet = new Set(prev);
      ids.forEach((id) => newSet.add(id));
      return newSet;
    });
  }, []);

  // Función para seleccionar producto por código de barras (escáner o código completo manual)
  const selectProductByBarcode = useCallback(
    (barcode: string) => {
      if (!selectedTransferDetail) return false;

      const normalized = normalizeScannedProductCode(barcode);
      if (!normalized) return false;

      // Keep parsed code visible in the scan field for cashier verification.
      setBarcodeInput(normalized);

      const lower = normalized.toLowerCase();
      const product = selectedTransferDetail.items.find(
        (item) =>
          (item.productCode || '').toLowerCase() === lower ||
          (item.recordCode || '').toLowerCase() === lower,
      );

      if (product) {
        toggleProductSelection(product.id);
        return true;
      }
      return false;
    },
    [selectedTransferDetail, toggleProductSelection],
  );

  // Cargar borrador cuando se abre el modal de aceptación
  useEffect(() => {
    const requireCounting = selectedTransferDetail?.requireCounting ?? true;
    if (
      showConfirmationDialog &&
      confirmationType === 'accept' &&
      transferIdToConfirm &&
      selectedTransferDetail &&
      requireCounting
    ) {
      const draft = loadDraft(transferIdToConfirm);

      if (draft && draft.selectedProductIds.length > 0) {
        // Verificar que los productos del borrador aún existen en la transferencia
        const validProductIds = draft.selectedProductIds.filter((id) =>
          selectedTransferDetail.items.some((item) => item.id === id),
        );

        if (validProductIds.length > 0) {
          setSelectedProductIds(new Set(validProductIds));
          setDraftLastSaved(new Date(draft.savedAt));
        } else {
          clearDraft(transferIdToConfirm);
        }
      } else {
        setSelectedProductIds(new Set());
        setDraftLastSaved(null);
      }
    } else if (!showConfirmationDialog) {
      // No limpiar al cerrar, mantener para cuando se reabra
    }
  }, [
    showConfirmationDialog,
    confirmationType,
    transferIdToConfirm,
    selectedTransferDetail,
    loadDraft,
    clearDraft,
  ]);

  // Computar métricas del dashboard
  const dashboardMetrics = useMemo(() => {
    const requireCounting = selectedTransferDetail?.requireCounting ?? true;
    if (!selectedTransferDetail || confirmationType !== 'accept' || !requireCounting) {
      return {
        total: 0,
        selected: 0,
        pending: 0,
        percentage: 0,
      };
    }

    const total = selectedTransferDetail.items.length;
    const selected = selectedProductIds.size;
    const pending = total - selected;
    const percentage = total > 0 ? Math.round((selected / total) * 100) : 0;

    return { total, selected, pending, percentage };
  }, [selectedTransferDetail, selectedProductIds, confirmationType]);

  // Computar si todos están seleccionados
  const allProductsSelected = useMemo(() => {
    const requireCounting = selectedTransferDetail?.requireCounting ?? true;
    // Si no se requiere conteo, siempre retornar true
    if (!requireCounting) return true;

    if (!selectedTransferDetail || confirmationType !== 'accept') return true;
    return (
      selectedTransferDetail.items.length > 0 &&
      selectedTransferDetail.items.every((item) => selectedProductIds.has(item.id))
    );
  }, [selectedTransferDetail, selectedProductIds, confirmationType]);

  // Agrupar productos por tipo; dentro de cada tipo, orden por código ascendente
  const groupedProductsByType = useMemo(() => {
    if (!selectedTransferDetail?.items || selectedTransferDetail.items.length === 0) {
      return {};
    }

    const grouped = groupBy(selectedTransferDetail.items, (item) => {
      const type = item.productType || 'Sin Tipo';
      return type;
    });

    const sorted: Record<string, TransferItem[]> = {};
    for (const key of Object.keys(grouped)) {
      sorted[key] = [...grouped[key]].sort((a, b) =>
        (a.productCode || '').localeCompare(b.productCode || '', undefined, {
          numeric: true,
          sensitivity: 'base',
        }),
      );
    }
    return sorted;
  }, [selectedTransferDetail]);

  // Obtener tipos de productos ordenados
  const productTypes = useMemo(() => {
    const types = Object.keys(groupedProductsByType);
    return types.sort((a, b) => a.localeCompare(b));
  }, [groupedProductsByType]);

  // Función para toggle de expansión de tipo
  const toggleProductTypeExpansion = useCallback((productType: string) => {
    setExpandedProductTypes((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(productType)) {
        newSet.delete(productType);
      } else {
        newSet.add(productType);
      }
      return newSet;
    });
  }, []);

  // Función para expandir todos los tipos
  const expandAllProductTypes = useCallback(() => {
    setExpandedProductTypes(new Set(productTypes));
  }, [productTypes]);

  // Función para colapsar todos los tipos
  const collapseAllProductTypes = useCallback(() => {
    setExpandedProductTypes(new Set());
  }, []);

  // Resetear grupos expandidos cuando se cierra el modal
  useEffect(() => {
    if (!showDetailsModal && !showConfirmationDialog) {
      setExpandedProductTypes(new Set());
    }
  }, [showDetailsModal, showConfirmationDialog]);

  // Función para cargar transferencias
  const fetchTransfers = useCallback(async () => {
    setIsLoading(true);
    try {
      const selectedLocationId = appliedFilters.location
        ? Number(appliedFilters.location)
        : undefined;
      const sourceLocationId = appliedFilters.sourceLocation
        ? Number(appliedFilters.sourceLocation)
        : undefined;
      const status = mapUiStatusToApiStatus(appliedFilters.status);

      const response = await InventoryTransfersService.inventoryTransfersControllerFindAll(
        pagination.currentPage,
        pagination.itemsPerPage,
        appliedFilters.transferCode || undefined,
        status,
        selectedLocationId,
        sourceLocationId,
        appliedFilters.dateFrom || undefined,
        appliedFilters.dateTo || undefined,
      );

      const responseData = response?.data ?? response;
      const transfers = Array.isArray(responseData?.data)
        ? responseData.data
        : Array.isArray(responseData)
          ? responseData
          : [];
      const meta = responseData?.meta;

      if (transfers.length > 0) {
        const formattedTransfers = transfers.map((transfer: any) => ({
          id: transfer.id?.toString() || '',
          transferCode: transfer.transferCode || `TR-${transfer.id}`,
          destination: transfer.destinationLocation?.name || 'No especificado',
          destinationLocationId:
            transfer.destinationLocation?.id != null
              ? Number(transfer.destinationLocation.id)
              : null,
          source: getTransferSourceDisplayName(transfer),
          productCount: Number(transfer.itemCount) || 0,
          date: formatTransferEventDateEs(transfer),
          status:
            transfer.status === 'PENDING'
              ? 'Pendiente'
              : transfer.status === 'COMPLETED'
                ? 'Completado'
                : transfer.status === 'CANCELLED'
                  ? 'Cancelado'
                  : 'Pendiente',
          createdBy: transfer.createdBy
            ? `${transfer.createdBy.firstName} ${transfer.createdBy.lastName}`
            : undefined,
          createdAt: transfer.createdAt || new Date().toISOString(),
        }));
        setTransferOrders(formattedTransfers);
        setPagination((prev) => ({
          ...prev,
          total: Number(meta?.total ?? formattedTransfers.length),
          currentPage: Number(meta?.page ?? prev.currentPage),
          itemsPerPage: prev.itemsPerPage,
          totalPages: Number(
            meta?.totalPages ??
              calculateTotalPages(
                Number(meta?.total ?? formattedTransfers.length),
                prev.itemsPerPage,
              ),
          ),
        }));
      } else {
        setTransferOrders([]);
        setPagination((prev) => ({
          ...prev,
          total: Number(meta?.total ?? 0),
          currentPage: Number(meta?.page ?? prev.currentPage),
          itemsPerPage: prev.itemsPerPage,
          totalPages: Number(meta?.totalPages ?? 0),
        }));
      }
    } catch (error) {
      console.error('Error al cargar transferencias:', error);
      setTransferOrders([]);
      setPagination((prev) => ({
        ...prev,
        total: 0,
        itemsPerPage: 10,
      }));
    } finally {
      setIsLoading(false);
    }
  }, [
    calculateTotalPages,
    appliedFilters.dateFrom,
    appliedFilters.dateTo,
    appliedFilters.location,
    appliedFilters.sourceLocation,
    appliedFilters.status,
    appliedFilters.transferCode,
    pagination.currentPage,
    pagination.itemsPerPage,
  ]);

  useEffect(() => {
    fetchTransfers();
  }, [fetchTransfers, searchTrigger]);

  const handleFilterChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const { name, value } = e.target;
      setFilters((prev) => ({
        ...prev,
        [name]: value,
      }));
    },
    [],
  );

  const handleSelectChange = useCallback(
    (e: React.ChangeEvent<{ name?: string; value: unknown }>) => {
      const name = e.target.name as string;
      const value = e.target.value as string;
      setFilters((prev) => ({
        ...prev,
        [name]: value,
      }));
    },
    [],
  );

  // Handle Enter key press for transfer code field
  const handleTransferCodeKeyPress = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSearch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleResetFilters = useCallback(async () => {
    setIsSearching(true);

    await new Promise((resolve) => setTimeout(resolve, 100));

    setFilters(EMPTY_FILTER_STATE);
    setAppliedFilters(EMPTY_FILTER_STATE);
    setPagination((prev) => ({
      ...prev,
      currentPage: 1,
    }));
    setSearchTrigger((prev) => prev + 1);

    setIsSearching(false);
  }, []);

  const handleSearch = useCallback(async () => {
    setIsSearching(true);

    await new Promise((resolve) => setTimeout(resolve, 100));

    setAppliedFilters(filters);
    setPagination((prev) => ({
      ...prev,
      currentPage: 1,
    }));
    setSearchTrigger((prev) => prev + 1);

    setIsSearching(false);
  }, [filters]);

  const clearAppliedFilterField = useCallback((field: keyof FilterState) => {
    setAppliedFilters((prev) => {
      const next = { ...prev, [field]: '' };
      setFilters(next);
      return next;
    });
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
    setSearchTrigger((prev) => prev + 1);
  }, []);

  /** Deep-link from related entities: filter list by transfer code and keep it after modal close. */
  const applyDeepLinkFilter = useCallback((transferCode: string) => {
    const nextFilters: FilterState = { ...EMPTY_FILTER_STATE, transferCode };
    setFilters(nextFilters);
    setAppliedFilters(nextFilters);
    setPagination((prev) => ({
      ...prev,
      currentPage: 1,
    }));
    setSearchTrigger((prev) => prev + 1);
  }, []);

  // Exports whatever is currently applied (last search), not the unsaved
  // draft in the filter inputs — matches what the list is showing.
  const handleExport = useCallback(async () => {
    setIsExporting(true);
    setNotification({ open: true, ...TRANSFER_MESSAGES.EXPORTING });
    try {
      await downloadCsvExport(
        '/inventory-transfers/export/csv',
        {
          transferCode: appliedFilters.transferCode || undefined,
          status: mapUiStatusToApiStatus(appliedFilters.status),
          destinationLocationId: appliedFilters.location
            ? Number(appliedFilters.location)
            : undefined,
          sourceLocationId: appliedFilters.sourceLocation
            ? Number(appliedFilters.sourceLocation)
            : undefined,
          dateFrom: appliedFilters.dateFrom || undefined,
          dateTo: appliedFilters.dateTo || undefined,
        },
        `transferencias-inventario-${csvExportDateSuffix()}.csv`,
      );
      setNotification({ open: true, ...TRANSFER_MESSAGES.EXPORTED_SUCCESS });
    } catch (error) {
      console.error('Error al exportar transferencias:', error);
      const errorMessage = error instanceof CsvExportError ? error.message : undefined;
      setNotification({ open: true, ...TRANSFER_MESSAGES.EXPORT_ERROR(errorMessage) });
    } finally {
      setIsExporting(false);
    }
  }, [appliedFilters]);

  const handleCloseModal = useCallback(() => {
    setShowDetailsModal(false);
    setSelectedTransferDetail(null);
  }, []);

  const handleViewDetails = useCallback(async (orderId: string) => {
    setIsLoadingDetails(true);
    setShowDetailsModal(true);

    try {
      const transferResponse =
        await InventoryTransfersService.inventoryTransfersControllerFindOne(orderId);
      const itemsResponse =
        await InventoryTransfersService.inventoryTransfersControllerFindTransferItems(orderId);

      const transfer = transferResponse?.data || transferResponse;
      const items = itemsResponse?.data || itemsResponse || [];

      if (transfer || items.length > 0) {
        const transferDetail = mapApiTransferToDetail(
          transfer,
          orderId,
          Array.isArray(items) ? items.map(mapApiTransferItemToTransferItem) : [],
        );

        setSelectedTransferDetail(transferDetail);
      } else {
        setSelectedTransferDetail(null);
      }
    } catch (error) {
      console.error('Error al cargar detalles de transferencia:', error);
      setSelectedTransferDetail(null);
    } finally {
      setIsLoadingDetails(false);
    }
  }, []);

  const handlePrintTransfer = useCallback(async (orderId: string) => {
    // Open popup in the same tick as the click so browsers do not block it after await.
    const printWindow = openBlankPrintWindow({
      size: 'document',
      windowName: 'printInventoryTransfer',
    });
    if (printWindow == null) {
      window.alert(PRINT_POPUP_BLOCKED_MESSAGE);
      return;
    }

    setIsLoadingDetails(true);

    try {
      const transferResponse =
        await InventoryTransfersService.inventoryTransfersControllerFindOne(orderId);
      const itemsResponse =
        await InventoryTransfersService.inventoryTransfersControllerFindTransferItems(orderId);

      const transfer = transferResponse?.data || transferResponse;
      const items = itemsResponse?.data || itemsResponse || [];

      if (transfer || items.length > 0) {
        const transferDetail = mapApiTransferToDetail(
          transfer,
          orderId,
          Array.isArray(items) ? items.map(mapApiTransferItemToTransferItem) : [],
        );
        printInventoryTransfer(transferDetail, printWindow);
      } else {
        printWindow.close();
      }
    } catch (error) {
      console.error('Error al cargar detalles de transferencia para impresión:', error);
      try {
        printWindow.close();
      } catch {
        // ignore
      }
    } finally {
      setIsLoadingDetails(false);
    }
  }, []);

  const handleAcceptTransfer = useCallback(async (orderId: string) => {
    await loadTransferDetailsForConfirmation(orderId, 'accept');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleRejectTransfer = useCallback(async (orderId: string) => {
    await loadTransferDetailsForConfirmation(orderId, 'reject');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadTransferDetailsForConfirmation = useCallback(
    async (orderId: string, type: 'accept' | 'reject') => {
      setIsLoadingDetails(true);

      try {
        const transferResponse =
          await InventoryTransfersService.inventoryTransfersControllerFindOne(orderId);
        const itemsResponse =
          await InventoryTransfersService.inventoryTransfersControllerFindTransferItems(orderId);

        const transfer = transferResponse?.data || transferResponse;
        const items = itemsResponse?.data || itemsResponse || [];

        if (transfer || items.length > 0) {
          const transferDetail = mapApiTransferToDetail(
            transfer,
            orderId,
            Array.isArray(items) ? items.map(mapApiTransferItemToTransferItem) : [],
            {
              requireCounting:
                transfer?.requireCounting !== undefined ? transfer.requireCounting : true,
            },
          );

          setSelectedTransferDetail(transferDetail);
          setTransferIdToConfirm(orderId);
          setConfirmationType(type);
          setShowConfirmationDialog(true);
        } else {
          console.error('No se pudieron cargar los detalles de la transferencia');
        }
      } catch (error) {
        console.error('Error al cargar detalles de transferencia para confirmación:', error);
      } finally {
        setIsLoadingDetails(false);
      }
    },
    [],
  );

  const handleOpenFinalConfirmation = useCallback(() => {
    if (!transferIdToConfirm) return;
    setShowFinalConfirmModal(true);
  }, [transferIdToConfirm]);

  const handleConfirmation = useCallback(async () => {
    if (!transferIdToConfirm) return;

    setIsConfirming(true);
    try {
      if (confirmationType === 'accept') {
        const confirmedTransferItemIds = selectedTransferDetail?.requireCounting
          ? selectedTransferDetail.items
              .filter((item) => selectedProductIds.has(item.id))
              .map((item) => Number(item.id))
          : undefined;

        await InventoryTransfersService.inventoryTransfersControllerCompleteTransfer(
          transferIdToConfirm,
          confirmedTransferItemIds != null ? { confirmedTransferItemIds } : {},
        );
        // Limpiar borrador después de aceptar con éxito
        clearDraft(transferIdToConfirm);
        setNotification({ open: true, ...TRANSFER_MESSAGES.ACCEPTED_SUCCESS });
      } else {
        await InventoryTransfersService.inventoryTransfersControllerCancelTransfer(
          transferIdToConfirm,
        );
        // Limpiar borrador si se rechaza
        clearDraft(transferIdToConfirm);
        setNotification({ open: true, ...TRANSFER_MESSAGES.REJECTED_SUCCESS });
      }
      setShowConfirmationDialog(false);
      setShowFinalConfirmModal(false);
      setTransferIdToConfirm(null);
      setSelectedProductIds(new Set());
      await fetchTransfers();
      onTransferUpdate?.();
    } catch (error) {
      console.error('Error al confirmar transferencia:', error);
      const errorMessage =
        (error as { body?: { message?: string } })?.body?.message ??
        (error as { message?: string })?.message;
      setNotification({
        open: true,
        ...TRANSFER_MESSAGES.CONFIRM_ERROR(errorMessage),
      });
      setShowFinalConfirmModal(false);
      setShowConfirmationDialog(false);
      setTransferIdToConfirm(null);
    } finally {
      setIsConfirming(false);
    }
  }, [
    transferIdToConfirm,
    confirmationType,
    clearDraft,
    fetchTransfers,
    onTransferUpdate,
    selectedProductIds,
    selectedTransferDetail,
  ]);

  const handleCancelConfirmation = useCallback(() => {
    setShowConfirmationDialog(false);
    setShowFinalConfirmModal(false);
    setTransferIdToConfirm(null);
  }, []);

  const handleCancelFinalConfirmation = useCallback(() => {
    setShowFinalConfirmModal(false);
  }, []);

  const handleCloseNotification = useCallback(() => {
    setNotification((prev) => ({ ...prev, open: false }));
  }, []);

  // Función toggle mejorada
  const toggleMoreFilters = useCallback(() => {
    setShowMoreFilters((prev) => !prev);
  }, []);

  // Función para formatear fechas
  const formatDate = useCallback((dateStr: string) => {
    const [datePart, timePart] = dateStr.split(', ');
    if (!datePart || !timePart) return 'Fecha inválida';

    const [day, month, year] = datePart.split('/');
    const formattedDate = new Date(`${year}-${month}-${day}T${timePart}`);
    return formattedDate.toLocaleString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  }, []);

  // Valores calculados
  const currentPageItems = useMemo(() => {
    return transferOrders;
  }, [transferOrders]);

  const visiblePages = useMemo(
    () => getVisiblePaginationItems(pagination.totalPages, pagination.currentPage),
    [pagination.totalPages, pagination.currentPage],
  );

  return {
    // State
    transferOrders,
    allTransferOrders: transferOrders,
    locations,
    filters,
    appliedFilters,
    isLoading,
    isSearching,
    isExporting,
    pagination,
    showMoreFilters,
    showDetailsModal,
    selectedTransferDetail,
    isLoadingDetails,
    showConfirmationDialog,
    notification,
    confirmationType,
    transferIdToConfirm,
    showFinalConfirmModal,
    isConfirming,

    // Conteo y borradores
    selectedProductIds,
    allProductsSelected,
    dashboardMetrics,
    draftSaved,
    draftLastSaved,
    isUpdatingCountingFlag,
    toggleProductSelection,
    selectProductIds,
    selectProductByBarcode,
    saveDraft,
    clearDraft,
    resetCounting,
    updateTransferRequireCounting,
    barcodeInput,
    setBarcodeInput,

    // Computed values
    currentPageItems,
    visiblePages,
    groupedProductsByType,
    productTypes,
    expandedProductTypes,

    // Handlers
    handleFilterChange,
    handleSelectChange,
    handleTransferCodeKeyPress,
    handleResetFilters,
    handleSearch,
    clearAppliedFilterField,
    applyDeepLinkFilter,
    handleExport,
    handleCloseModal,
    handleViewDetails,
    handlePrintTransfer,
    handleAcceptTransfer,
    handleRejectTransfer,
    handleOpenFinalConfirmation,
    handleConfirmation,
    handleCancelConfirmation,
    handleCancelFinalConfirmation,
    handleCloseNotification,
    toggleMoreFilters,
    handlePageChange,
    handleItemsPerPageChange,
    toggleProductTypeExpansion,
    expandAllProductTypes,
    collapseAllProductTypes,

    // Utilities
    formatDate,
  };
};
